import React, { useEffect, useState } from "react";
import { Loader2, Save, ArrowLeft, Plus, X, Trash2, Edit, RefreshCw, Layers } from "lucide-react";
import axios from "../../api/axios";
import toast from "react-hot-toast";

// Import unified PaginationSearchbar component
import PaginationSearchbar from "../../GlobalComponents/PaginationSearchbar";

// Import central theme classes from MasterCssClass.js
import {
  masterFormCard,
  masterFormFieldGroup,
  masterInputLabel,
  masterInput,
  masterSelect,
  masterFormBtnSubmit,
  masterBtnBack,
  masterPanelCard,
  masterHeadingMain,
  masterTableWrap,
  masterTable,
  masterTableHead,
  masterTableRow,
  masterBadgeLive,
  masterBadgeInactive,
  masterActionBtnEdit,
  masterActionBtnDelete,
  masterPageContainer,
  masterHeaderContent,
  masterHeaderBrand,
  masterHeaderIconBox,
  masterHeaderActions,
  masterTableContainer,
  masterThCode,
  masterThName,
  masterThStatus,
  masterThManage,
  masterTdCode,
  masterTdName,
  masterTdStatus,
  masterTdManage,
  masterTdManageFlex,
  masterTdEmpty,
} from "../../GlobalComponents/MasterCssClass";

const CategoryMaster = () => {
  // View mode state: "form" (default) or "list"
  const [viewMode, setViewMode] = useState("form");

  const [editId, setEditId] = useState(null);
  const isEdit = Boolean(editId);

  const [exams, setExams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // List filter and pagination states
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [formData, setFormData] = useState({
    examCode: "",
    examStages: [],
    examStageInput: "",
    catName: "",
    features: [],
    status: "ACTIVE",
  });

  const featureOptions = [
    "SYLLABUS",
    "MOCK TEST",
    "PRACTICE SET",
    "PYQ",
    "STUDY MATERIAL",
    "VIDEOS",
  ];

  const fetchBase = async () => {
    setLoading(true);
    try {
      const [exRes, catRes] = await Promise.allSettled([
        axios.get("/master/exam"),
        axios.get("/master/category/all"),
      ]);

      const examList =
        exRes.status === "fulfilled"
          ? Array.isArray(exRes.value.data)
            ? exRes.value.data
            : exRes.value.data?.data || []
          : [];
      const categoryList =
        catRes.status === "fulfilled"
          ? Array.isArray(catRes.value.data)
            ? catRes.value.data
            : catRes.value.data?.data || []
          : [];

      setExams(examList);
      setCategories(categoryList);
    } catch {
      toast.error("DATABASE SYNC FAILED");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBase();
  }, []);

  const toggleFeature = (feature) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter((f) => f !== feature)
        : [...prev.features, feature],
    }));
  };

  const addExamStage = () => {
    const value = String(formData.examStageInput || "").trim().toUpperCase();
    if (!value) return;
    setFormData((prev) => ({
      ...prev,
      examStages: prev.examStages.includes(value)
        ? prev.examStages
        : [...prev.examStages, value],
      examStageInput: "",
    }));
  };

  const updateExamStage = (idx, value) => {
    const clean = String(value || "").toUpperCase();
    setFormData((prev) => ({
      ...prev,
      examStages: prev.examStages.map((s, i) => (i === idx ? clean : s)),
    }));
  };

  const removeExamStage = (idx) => {
    setFormData((prev) => ({
      ...prev,
      examStages: prev.examStages.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.examCode) return toast.error("PLEASE SELECT EXAM");
    if (!formData.catName?.trim())
      return toast.error("PLEASE ENTER CATEGORY NAME");

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        id: isEdit ? editId : undefined,
        examStages: formData.examStages.filter(Boolean),
      };
      const res = await axios.post("/master/category/upsert", payload);
      if (res.data.success) {
        toast.success(isEdit ? "UPDATED" : "SAVED");
        setEditId(null);
        setFormData({
          examCode: "",
          examStages: [],
          examStageInput: "",
          catName: "",
          features: [],
          status: "ACTIVE",
        });
        fetchBase();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "SAVE FAILED");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (item) => {
    const matchedExam = exams.find(
      (ex) =>
        (item.examCode && ex.examCode === item.examCode) ||
        (item.examName && ex.examName === item.examName) ||
        String(item.examId || "") === String(ex._id || "")
    );
    const stages = Array.from(
      new Set(
        [
          ...(Array.isArray(item.examStages) ? item.examStages : []),
          item.examStage,
        ]
          .map((s) => String(s || "").trim().toUpperCase())
          .filter(Boolean)
      )
    );
    setEditId(item._id);
    setFormData({
      examCode: matchedExam?.examCode || item.examCode || "",
      examStages: stages,
      examStageInput: "",
      catName: item.catName || "",
      features: item.features || [],
      status: item.status || "ACTIVE",
    });
    setViewMode("form");
  };

  const handleDelete = async (id) => {
    if (window.confirm("ARE YOU SURE YOU WANT TO DELETE THIS RECORD?")) {
      try {
        await axios.delete(`/master/category/${id}`);
        toast.success("DELETED SUCCESSFULLY");
        fetchBase();
      } catch {
        toast.error("DELETE FAILED");
      }
    }
  };

  const filteredData = categories.filter((item) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const startsWithFirstLetter = (value) => {
      if (!value) return false;
      return value.trim().toLowerCase().startsWith(term);
    };
    const stagesStr = Array.isArray(item.examStages)
      ? item.examStages.join(" ")
      : item.examStage || "";
    return (
      startsWithFirstLetter(item.catName) ||
      startsWithFirstLetter(item.catId) ||
      startsWithFirstLetter(item.examName) ||
      startsWithFirstLetter(item.examCode) ||
      startsWithFirstLetter(stagesStr) ||
      startsWithFirstLetter(item.status || "ACTIVE")
    );
  });

  const isAll = rowsPerPage === "ALL" || rowsPerPage === -1;
  const startIndex = isAll ? 0 : (currentPage - 1) * rowsPerPage;
  const paginatedData = isAll
    ? filteredData
    : filteredData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <div className={`${masterPageContainer} h-full flex flex-col overflow-y-auto custom-scrollbar`}>
      {viewMode === "form" ? (
        <div>
          {/* HEADER CARD FOR FORM VIEW MATCHING THE EXACT TOP-RIGHT "ADD NEW" PANEL POSITION */}
          <div className={masterPanelCard}>
            <div className={masterHeaderContent}>
              <div className={masterHeaderBrand}>
                <div className={masterHeaderIconBox}>
                  <Layers size={13} />
                </div>
                <h1 className={masterHeadingMain}>{isEdit ? "Edit Category" : "Category Form"}</h1>
              </div>

              <div className={masterHeaderActions}>
                <button
                  type="button"
                  onClick={() => {
                    setEditId(null);
                    setFormData({
                      examCode: "",
                      examStages: [],
                      examStageInput: "",
                      catName: "",
                      features: [],
                      status: "ACTIVE",
                    });
                    setViewMode("list");
                  }}
                  className={masterFormBtnSubmit}
                >
                  <Plus size={12} strokeWidth={3} />
                  <span>VIEW LIST</span>
                </button>
              </div>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className={`${masterFormCard} flex flex-col gap-3 mt-3`}
          >
            {/* ROW 1: EXAM, CATEGORY NAME, STATUS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 w-full">
              {/* Exam */}
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Exam</label>
                <select
                  value={formData.examCode}
                  onChange={(e) =>
                    setFormData({ ...formData, examCode: e.target.value })
                  }
                  className={masterSelect}
                >
                  <option value="">-- SELECT EXAM --</option>
                  {exams.map((ex) => (
                    <option key={ex.examCode} value={ex.examCode}>
                      {ex.examName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category Name */}
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Category Name</label>
                <input
                  type="text"
                  value={formData.catName}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      catName: e.target.value.toUpperCase(),
                    })
                  }
                  placeholder="ENTER CATEGORY NAME"
                  className={masterInput}
                />
              </div>

              {/* Status */}
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  className={masterSelect}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>
            </div>

            {/* ROW 2: EXAM STAGES BUILDER */}
            <div className="flex flex-col gap-1.5 p-1 bg-slate-50 border border-slate-200 rounded-lg">
              <label className={masterInputLabel}>Exam Stages</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={formData.examStageInput}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      examStageInput: e.target.value.toUpperCase(),
                    })
                  }
                  placeholder="ADD STAGE (E.G. PRELIMS, MAINS)"
                  className={`${masterInput} flex-1`}
                />
                <button
                  type="button"
                  onClick={addExamStage}
                  className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                >
                  <Plus size={14} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.examStages.map((stage, idx) => (
                  <div
                    key={`${stage}-${idx}`}
                    className="flex items-center gap-1 bg-white border border-slate-300 px-2 py-0.5 rounded text-xs"
                  >
                    <input
                      type="text"
                      value={stage}
                      onChange={(e) => updateExamStage(idx, e.target.value)}
                      className="w-24 outline-none font-semibold text-slate-700 uppercase"
                    />
                    <button
                      type="button"
                      onClick={() => removeExamStage(idx)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {formData.examStages.length === 0 && (
                  <span className="text-[11px] text-slate-400 italic">
                    No stages added yet.
                  </span>
                )}
              </div>
            </div>

            {/* ROW 3: FEATURES CHECKBOXES */}
            <div className="flex flex-col gap-1.5 p-1 bg-white border border-slate-200 rounded-lg">
              <label className={masterInputLabel}>Features</label>
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
                {featureOptions.map((feature) => (
                  <label
                    key={feature}
                    className="flex items-center gap-1.5 text-xs font-bold text-slate-700 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.features.includes(feature)}
                      onChange={() => toggleFeature(feature)}
                      className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span>{feature}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* ROW 4: BUTTONS (LEFT BACK, RIGHT SAVE) */}
            <div className="flex items-center justify-between w-full">
              <button
                type="button"
                onClick={() => {
                  setEditId(null);
                  setFormData({
                    examCode: "",
                    examStages: [],
                    examStageInput: "",
                    catName: "",
                    features: [],
                    status: "ACTIVE",
                  });
                  setViewMode("list");
                }}
                className={`${masterBtnBack} flex items-center justify-center gap-1.5 h-[26px]`}
              >
                <ArrowLeft size={14} />
                <span>BACK</span>
              </button>

              <div>
                <button
                  disabled={isSaving || loading}
                  type="submit"
                  className={masterFormBtnSubmit}
                >
                  {isSaving ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Save size={14} />
                  )}
                  {isEdit ? "UPDATE" : "SAVE"}
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex flex-col flex-1 h-full">
          {/* HEADER CARD */}
          <div className={masterPanelCard}>
            <div className={masterHeaderContent}>
              <div className={masterHeaderBrand}>
                <div className={masterHeaderIconBox}>
                  <Layers size={13} />
                </div>
                <h1 className={masterHeadingMain}>Category List</h1>
              </div>

              <div className={masterHeaderActions}>
                <button
                  type="button"
                  onClick={fetchBase}
                  className={masterBtnBack}
                  title="Refresh Records"
                >
                  <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                  <span>SYNC</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setEditId(null);
                    setFormData({
                      examCode: "",
                      examStages: [],
                      examStageInput: "",
                      catName: "",
                      features: [],
                      status: "ACTIVE",
                    });
                    setViewMode("form");
                  }}
                  className={masterFormBtnSubmit}
                >
                  <Plus size={12} strokeWidth={3} />
                  <span>ADD NEW</span>
                </button>
              </div>
            </div>
          </div>

          {/* COMBINED SEARCH & PAGINATION BAR */}
          <PaginationSearchbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="SEARCH CATEGORY ID, NAME, OR EXAM BY FIRST LETTER..."
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={setRowsPerPage}
            totalItems={filteredData.length}
          />

          {/* TABLE */}
          <div className={`${masterTableContainer} flex-1 min-h-0 flex flex-col`}>
            <div className={`${masterTableWrap} overflow-x-auto overflow-y-auto custom-scrollbar flex-1 max-h-[calc(100vh-260px)]`}>
              <table className={masterTable}>
                <thead>
                  <tr className={masterTableHead}>
                    <th className={masterThCode}>SL NO</th>
                    <th className={masterThName}>EXAM NAME</th>
                    <th className={masterThName}>CATEGORY NAME</th>
                    <th className={masterThName}>EXAM STAGE</th>
                    <th className={masterThName}>FEATURES</th>
                    <th className={masterThStatus}>STATUS</th>
                    <th className={masterThManage}>MANAGE</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className={masterTdEmpty}>
                        Syncing Database...
                      </td>
                    </tr>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((item,index) => (
                      <tr key={item._id} className={masterTableRow}>
                        <td className={masterTdCode}>{startIndex + index + 1}</td>
                        <td className={masterTdName}>{item.examName || "---"}</td>
                        <td className={masterTdName}>{item.catName}</td>
                        <td className={masterTdName}>
                          {(Array.isArray(item.examStages)
                            ? item.examStages
                            : [item.examStage]
                          )
                            .filter(Boolean)
                            .join(", ") || "---"}
                        </td>
                        <td className={masterTdName}>
                          <div className="flex flex-wrap gap-1">
                            {(item.features || []).slice(0, 2).map((f) => (
                              <span
                                key={f}
                                className="px-1.5 py-0.5 bg-slate-100 text-slate-800 rounded text-[10px] font-bold"
                              >
                                {f}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className={masterTdStatus}>
                          <span
                            className={
                              item.status === "INACTIVE"
                                ? masterBadgeInactive
                                : masterBadgeLive
                            }
                          >
                            {item.status || "ACTIVE"}
                          </span>
                        </td>
                        <td className={masterTdManage}>
                          <div className={masterTdManageFlex}>
                            <button
                              type="button"
                              onClick={() => handleEdit(item)}
                              className={masterActionBtnEdit}
                              title="Edit"
                            >
                              <Edit size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(item._id)}
                              className={masterActionBtnDelete}
                              title="Delete"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className={masterTdEmpty}>
                        No Records Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryMaster;